/**
 * Human titles for the owner actions the audit trail records.
 *
 * The Activity tab renders a logged action by looking its name up here. An
 * action written without a title would fall back to its raw machine name
 * ("management_invoice_proof_deleted") in front of an operator — which is the
 * regression owner-audit-titles.test.ts exists to catch.
 *
 * Mirrors src/components/admin/v2/tenant/tenant-audit-titles.ts, deliberately:
 * the two profiles should read the same way.
 */

export const OWNER_AUDIT_ACTION_TITLES: Record<string, string> = {
  owner_updated: "Owner details updated",
  owner_login_link_sent: "Login link sent",
  owner_document_uploaded: "Document uploaded",
  owner_document_deleted: "Document deleted",
  management_invoice_sent: "Invoice sent to owner",
  management_invoice_marked_paid: "Invoice marked paid",
  management_invoice_marked_unpaid: "Invoice payment mark undone",
  management_invoice_proof_uploaded: "Payment proof uploaded",
  management_invoice_proof_deleted: "Payment proof deleted",
  management_invoice_note_added: "Payment note added",
  // Settling a company_collects invoice against the rent CoLiviq collected —
  // the offset equivalent of "marked paid", kept as its own action so the
  // timeline says which way the money actually moved.
  management_invoice_offset_confirmed: "Invoice settled against rent",
  // The Storno pair, written together by one RPC. Both entries carry the other
  // invoice's number, so the timeline reads as "…-001 cancelled" followed by
  // "…-002 issued to replace it" rather than as an invoice that vanished.
  management_invoice_canceled: "Invoice cancelled",
  management_invoice_reissued: "Corrected invoice issued",
  // Written by the database triggers in
  // 20260817110000_owner_activity_captures_every_actor.sql, which record every
  // writer — including the owner themselves, whose changes the audit_logs
  // INSERT policy had been rejecting outright.
  owner_created: "Owner record created",
  owner_deleted: "Owner record deleted",
  owner_account_updated: "Portal account updated",
  owner_payout_details_updated: "Payout details updated",
  owner_property_link_added: "Linked to a property",
  owner_property_link_removed: "Unlinked from a property",
  owner_property_link_updated: "Property link updated",
  // The records that BELONG to an owner, from the triggers in
  // 20260817130000. An owner who gained an apartment, was invited to onboard
  // and started a subscription had none of it on their timeline before.
  owner_apartment_added: "Apartment added to portfolio",
  owner_apartment_removed: "Apartment left portfolio",
  owner_apartment_updated: "Apartment terms updated",
  owner_property_added: "Property added to portfolio",
  owner_property_removed: "Property left portfolio",
  owner_property_updated: "Property updated",
  owner_subscription_added: "Subscription started",
  owner_subscription_removed: "Subscription removed",
  owner_subscription_updated: "Subscription updated",
  owner_onboarding_added: "Onboarding invitation sent",
  owner_onboarding_removed: "Onboarding invitation withdrawn",
  owner_onboarding_updated: "Onboarding invitation updated",
  owner_onboarding_submission_added: "Onboarding submitted",
  owner_onboarding_submission_removed: "Onboarding submission removed",
  owner_onboarding_submission_updated: "Onboarding submission updated",
  // Distinct from owner_document_* above, which are the documents CoLiviq
  // uploads onto the owner record. These two are the owner's own: what they
  // upload against an apartment, and their account documents.
  owner_apartment_document_added: "Apartment document uploaded",
  owner_apartment_document_removed: "Apartment document deleted",
  owner_apartment_document_updated: "Apartment document renamed",
  owner_account_document_added: "Account document added",
  owner_account_document_removed: "Account document deleted",
  owner_account_document_updated: "Account document renamed",
};

/**
 * How an actor's role reads next to their name.
 *
 * The point of the label is telling apart "we changed this" from "the owner
 * changed this themselves" at a glance — the distinction that makes the feed
 * usable when something looks wrong and nobody remembers touching it.
 */
const ACTOR_ROLE_LABELS: Record<string, string> = {
  super_admin: "Super admin",
  admin: "Admin",
  tech: "Tech",
  owner: "Owner",
  user: "Tenant",
  // No session behind the write: a cron tick, or an edge function running on
  // the service role. Named rather than left blank, so an unattributed change
  // reads as "the system did this" instead of as missing data.
  system: "System",
};

export function ownerActorRoleLabel(role: unknown): string | null {
  return typeof role === "string" ? (ACTOR_ROLE_LABELS[role] ?? null) : null;
}

/**
 * "Vishnu (Admin)", "Pascal Fehst (Owner)", or "System".
 *
 * A name on its own does not say in which capacity someone acted, and the role
 * on its own does not say who — an audit line needs both, and needs to degrade
 * to whichever half it has rather than rendering an empty byline.
 */
export function ownerActorLabel(
  name: string | null | undefined,
  role: unknown
): string | null {
  const who = name?.trim() || null;
  const what = ownerActorRoleLabel(role);
  if (who && what) return `${who} (${what})`;
  return who ?? what;
}

/** The fixed title for an action, or null when the action is unknown here. */
export function ownerAuditActionTitle(action: string): string | null {
  return OWNER_AUDIT_ACTION_TITLES[action] ?? null;
}

/**
 * Titles for the entry that covers BOTH of an owner's records.
 *
 * An owner is stored twice, and a name or phone change to one is mirrored onto
 * the other in the same transaction. That is one action, so it gets one entry
 * (the mirrored write records nothing — see
 * 20260817120000_collapse_mirrored_owner_activity.sql) — and the entry has to
 * say so, or "Portal account updated" would read as if the owner record, and
 * therefore the next invoice, still had the old details.
 */
const OWNER_MIRRORED_TITLES: Record<string, string> = {
  owner_account_updated: "Account updated — owner record updated to match",
  owner_updated: "Owner details updated — account updated to match",
};

export function ownerMirroredActionTitle(action: string): string | null {
  return OWNER_MIRRORED_TITLES[action] ?? null;
}

/** Field labels for the `owner_updated` diff, so the feed names what changed. */
export const OWNER_FIELD_LABELS: Record<string, string> = {
  name: "Name",
  type: "Owner type",
  address: "Address",
  city_postal: "City / postal code",
  email: "Email",
  phone: "Phone",
  rep_name: "Representative",
  rep_role: "Representative role",
  rep_note: "Representative note",
  bank_holder: "Account holder",
  bank_iban: "IBAN",
  bank_bic: "BIC",
  bank_name: "Bank",
  is_platform: "coliviq-held account",
  // Portal-account fields (profiles), reached by owner_account_updated.
  date_of_birth: "Date of birth",
  is_active: "Account active",
  billing_address: "Billing address",
  billing_city_postal: "Billing city / postal code",
  // Payout fields (property_owners), reached by owner_payout_details_updated.
  bank_account_name: "Payout account holder",
  bank_account_iban: "Payout IBAN",
  bank_account_bic: "Payout BIC",
  property_id: "Property",
  lessor_id: "Owner record",
  contract_required: "Contract required",
};

export function ownerFieldLabel(field: string): string {
  return OWNER_FIELD_LABELS[field] ?? field;
}

/** "—" for an unset value, so a cleared field reads as cleared, not as blank. */
export function ownerFieldValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}
